import {ErrorMessage, Field, Form, Formik} from 'formik'
import {useIntl} from 'react-intl'
import SVG from 'react-inlinesvg'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {CatalogConfig, CatalogItem} from '../types'
import {buildInitialValues, buildValidationSchema} from '../utils/catalogValidation'

type Props = {
  config: CatalogConfig
  // When editing, the existing item; when creating, undefined.
  item?: CatalogItem
  onClose: () => void
  onSubmit: (values: Record<string, string>) => Promise<void>
}

// Generic create/edit modal driven by config.fields. Mirrors the custom nb-modal-overlay +
// Formik/Yup + <Field>/<ErrorMessage> pattern used by the manage-admins modals. The parent
// owns persistence (onSubmit) and toasts; this component only validates and collects input.
export const CatalogFormModal = ({config, item, onClose, onSubmit}: Props) => {
  const intl = useIntl()
  const isEdit = Boolean(item)
  const itemLabel = intl.formatMessage({id: config.singularId})
  const translate = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({id}, values)

  return (
    <div className='nb-modal-overlay' onClick={onClose}>
      <div className='nb-invite-modal' onClick={(e) => e.stopPropagation()}>
        <div className='admin-experience-details-header'>
          <h2 className='nb-invite-modal__title'>
            {intl.formatMessage(
              {id: isEdit ? 'CATALOG.MODAL.EDIT_TITLE' : 'CATALOG.MODAL.CREATE_TITLE'},
              {item: itemLabel}
            )}
          </h2>
          <div className='rm-style-3 cursor-pointer' onClick={onClose}>
            <SVG src={toAbsoluteUrl('/media/svg/nobilis/closeX.svg')} className='rm-style-4' />
          </div>
        </div>

        <Formik
          enableReinitialize
          initialValues={buildInitialValues(config, item)}
          validationSchema={buildValidationSchema(config, translate)}
          onSubmit={async (values, actions) => {
            try {
              await onSubmit(values)
            } finally {
              actions.setSubmitting(false)
            }
          }}
        >
          {({isSubmitting}) => (
            <Form className='profile-admin-form profile-form-base' style={{display: 'contents'}}>
              <div className='team-admin-add-member__card'>
                {config.fields.map((field) => (
                  <div className='form-field' key={field.name}>
                    <label className='field-label'>{intl.formatMessage({id: field.labelId})}</label>
                    {field.type === 'textarea' ? (
                      <Field name={field.name} as='textarea' className='input' rows={3} />
                    ) : field.type === 'select' ? (
                      <Field name={field.name} as='select' className='input'>
                        <option value=''>—</option>
                        {(field.options ?? []).map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.labelId ? intl.formatMessage({id: opt.labelId}) : opt.value}
                          </option>
                        ))}
                      </Field>
                    ) : (
                      <Field name={field.name} className='input' />
                    )}
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block input-text-style fs-8'>
                        <ErrorMessage name={field.name} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className='form-actions'>
                <button type='button' className='btn-secondary' onClick={onClose}>
                  {intl.formatMessage({id: 'CATALOG.MODAL.CANCEL'})}
                </button>
                <button
                  type='submit'
                  className='btn nb-btn-primary'
                  disabled={isSubmitting}
                  aria-busy={isSubmitting ? 'true' : 'false'}
                  aria-live='polite'
                >
                  {!isSubmitting ? (
                    <span className='nb-heading-md'>
                      {intl.formatMessage({id: 'CATALOG.MODAL.SAVE'})}
                    </span>
                  ) : (
                    <span className='indicator-progress nb-heading-md'>
                      {intl.formatMessage({id: 'CATALOG.LOADING'})}
                      <span
                        className='spinner-border spinner-border-sm align-middle ms-2'
                        role='status'
                        aria-hidden='true'
                      />
                    </span>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  )
}
